<template>
    <a v-if="displayRequest" class="btn btn-sm btn-primary" target="_blank" rel="noopener" :href="value.url_link">
        {{t('request')}}
    </a>
    <div v-else-if="displayForbiddenOrder"
         class="btn btn-sm btn-primary d-inline-flex btn-cart-disabled pe-0 align-items-center">
        <div class="flex-grow-1" :class="{'pe-3': !displayForbiddenOrderLimitMessage}">
            <span v-if="isOrderedProduct">{{t('ordered.product')}}</span>
            <span v-else>{{orderedType}}</span>
        </div>
        <div v-if="displayForbiddenOrderLimitMessage" class="flex-grow-0 px-2">
            <products-product-limit :value="value"/>
        </div>
    </div>
    <router-link v-else-if="forceProductPage" class="btn btn-sm btn-primary btn-cart" :to="route">
        {{t('view')}}
    </router-link>
    <button v-else-if="displayAdd"
            type="button"
            class="btn btn-sm btn-primary"
            :disabled="disableAdd || processing"
            @click="add">
        <span v-if="processing" class="icon-spinner animate-spin"></span>
        <span>{{t('add')}}</span>
    </button>
    <button v-else-if="displayRemove"
            type="button"
            class="btn btn-sm btn-outline-primary"
            :disabled="processing"
            @click="remove">
        <span v-if="processing" class="icon-spinner animate-spin"></span>
        <span>{{t('remove')}}</span>
    </button>
</template>

<script>

    import mixin_cart from "@/mixins/cart";
    import mixin_constraints from "@/mixins/constraints";
    import mixin_products from "@/mixins/products";
    import Spinner from "@/components/Spinner";

    import {PRODUCT_CLASS} from "@/constants";
    import ProductsProductLimit from "./ProductsProductLimit";
    import emitter from "@/api/events";

    export default {
        name: "ProductsProductCart",
        components: {
            ProductsProductLimit,
            Spinner
        },
        mixins: [mixin_cart, mixin_constraints, mixin_products],
        emits: ["add", "remove"],
        props: {
            value: {
                type: Object,
                required: true
            },
            grouping: String,
            groupingId: [Number, String],
            displayAddToast: Boolean,
            handleForceDetailPage: Boolean
        },
        data() {

            return {
                t_path: "components.products.cart",
                processing: false,
                productsLoading: false,
                state: {
                    isOrderedProduct: null,
                    displayForbiddenOrder: null,
                    displayAdd: null,
                    disableAdd: null,
                    displayRemove: null
                }
            };
        },
        computed: {
            isCartLoading() {

                return this.processing || this.productsLoading || this.$store.getters["user/cart/loading"];
            },
            isLoading() {

                return this.isCartLoading || this.$store.getters["user/orders/loading"] || this.$store.getters["user/constraints/loading"];
            },
            isOrderedProduct() {

                return this.isLoading
                    ? this.state.isOrderedProduct
                    : this.products_productOrdered(this.value);
            },
            displayRequest() {

                return this.value.class && PRODUCT_CLASS.LINKED_PRODUCT === this.value.class;
            },
            displayForbiddenOrderLimitMessage() {

                return this.products_displayProductLimitMessage(this.value);
            },
            displayForbiddenOrder() {

                if (this.isLoading) {

                    return this.state.displayForbiddenOrder;
                }

                if (false === this.$store.getters["user/cart/exists"](this.value)) {

                    if (
                        "undefined" !== typeof this.value.allowed_to_order
                        && false === this.value.allowed_to_order
                        && 0 === this.constraints_filterAvailableProducts([this.value]).length) {

                        return true;
                    }

                    if (this.value.allowed_to_order_reason?.code) {

                        if (
                            "cart" === this.value.allowed_to_order_reason.code
                            || -1 !== this.value.allowed_to_order_reason.code.indexOf("cart")
                        ) {

                            return false;
                        }

                        return true;

                    }
                }

                return false;
            },
            displayAdd() {

                return this.isLoading
                    ? this.state.displayAdd
                    : (
                        0 < this.constraints_filterAvailableProducts([this.value], true).length
                        || 1 < this.constraints_getProductTypeAvailableAmount(this.value)
                        || !this.$store.getters["user/cart/exists"](this.value)
                    );
            },
            disableAdd() {

                return this.isLoading
                    ? this.state.disableAdd
                    : (
                        0 === this.constraints_filterAvailableProducts([this.value], true).length
                        || (
                            this.value.allowed_to_order_reason?.code
                            && (
                                "cart" === this.value.allowed_to_order_reason.code
                                || -1 !== this.value.allowed_to_order_reason.code.indexOf("cart")
                            )
                        )
                    );
            },
            displayRemove() {

                return this.isLoading
                    ? this.state.displayRemove
                    : (
                        1 === this.constraints_getProductTypeAvailableAmount(this.value)
                        && !this.displayAdd
                        && this.$store.getters["user/cart/exists"](this.value)
                    );
            },
            type() {

                return this.products_translateProductType(this.value).toLowerCase();
            },
            orderedType() {

                if (
                    this.value.allowed_to_order_reason
                    && this.value.allowed_to_order_reason.code
                    && (
                        "string" === typeof this.value.allowed_to_order_reason.code
                        || 0 < this.value.allowed_to_order_reason.code.length
                    )
                ) {

                    const errorCode = "string" === typeof this.value.allowed_to_order_reason.code
                        ? this.value.allowed_to_order_reason.code
                        : this.value.allowed_to_order_reason.code[0];

                    if (
                        this.value.allowed_to_order_reason.conflicting_products
                        && 0 < this.value.allowed_to_order_reason.conflicting_products.length
                    ) {

                        const conflictingProducts = this.value.allowed_to_order_reason.conflicting_products.map(
                            product => this.products_translateProductName(
                                product,
                                ["genericName", "generic_name", "name"]
                            )
                        );

                        return this.t(`ordered.error.${errorCode}`, conflictingProducts.length, {
                            named: {name: conflictingProducts.join(", ")}
                        });
                    }

                    return this.t(`ordered.error.${errorCode}`);
                }

                if (1 < this.value.product_type.length) {

                    return this.t("ordered.type.default_multiple");
                }

                for (let i = 0; i < this.value.product_type.length; i++) {

                    if (true === this.te(`ordered.type.${this.value.product_type[i]}`)) {

                        return this.t(`ordered.type.${this.value.product_type[i]}`);
                    }
                }

                return this.t("ordered.type.default");
            },
            forceProductPage() {

                return true === this.handleForceDetailPage
                    && 1 === parseInt(process.env.VUE_APP_CATALOG_FORCE_PRODUCT_PAGE);
            },
            route() {

                return {
                    name: "products.product",
                    params: {
                        grouping: this.grouping,
                        grouping_id: this.groupingId,
                        sku: this.value.sku
                    }
                };
            }
        },
        methods: {
            remove() {

                if (true === this.isCartLoading) {

                    return false;
                }
                this.processing = true;

                this.cart_remove(this.value)
                    .finally(() => this.processing = false);

                this.$emit("remove");
            },
            add() {

                if (true === this.isCartLoading || true === this.disableAdd) {

                    return false;
                }

                this.processing = true;

                this.cart_add(this.value)
                    .then(() => {

                        if (this.displayAddToast) {

                            this.$toast.success(this.t("added"));
                        }
                    })
                    .finally(() => this.processing = false);

                this.$emit("add")
            },
            setProductsLoading() {

                this.productsLoading = true;
            },
            unsetProductsLoading() {

                this.productsLoading = false;
            }
        },
        watch: {
            isLoading: {
                immediate: true,
                handler(newValue) {

                    if (false === newValue) {

                        this.state.isOrderedProduct = this.isOrderedProduct;
                        this.state.displayForbiddenOrder = this.displayForbiddenOrder;
                        this.state.displayAdd = this.displayAdd;
                        this.state.disableAdd = this.disableAdd;
                        this.state.displayRemove = this.displayRemove;
                    }
                }
            }
        },
        mounted() {

            emitter.on('grouping_product_before_load', this.setProductsLoading);
            emitter.on('grouping_products_before_load', this.setProductsLoading);

            emitter.on('grouping_product_after_load', this.unsetProductsLoading);
            emitter.on('grouping_products_after_load', this.unsetProductsLoading);

        },
        beforeUnmount() {

            emitter.off('grouping_product_before_load', this.setProductsLoading);
            emitter.off('grouping_products_before_load', this.setProductsLoading);

            emitter.off('grouping_product_after_load', this.unsetProductsLoading);
            emitter.off('grouping_products_after_load', this.unsetProductsLoading);
        }
    }
</script>